import { __awaiter, __decorate, __param, __rest } from "tslib";
import { DI, optional } from '@microsoft/fast-foundation';
import jsCookie from 'js-cookie';
import { AuthType } from '../auth/auth.types';
import { Session } from '../session';
import { logger } from '../utils';
import { CredentialManagerConfig } from './credentialManager.types';
function supportsCredentialsContainer() {
    if (!(navigator === null || navigator === void 0 ? void 0 : navigator.credentials)) {
        logger.warn('CredentialsContainer not available in this browser.');
        return false;
    }
    if (!('PasswordCredential' in window)) {
        logger.warn('PasswordCredential not available in this browser.');
        return false;
    }
    return true;
}
/**
 * The default separator used for splitting credential parts.
 * @public
 */
export const credentialSeparator = ':';
function extractParts(input = '', separator = credentialSeparator, limit) {
    return input
        .split(separator, limit)
        .map((opt) => opt.trim())
        .filter((opt) => opt !== '');
}
function includesSeparator(input, separator = credentialSeparator) {
    const value = typeof input == 'string' && input.trim();
    return !!value && value.includes(separator);
}
function countSeparators(input, separator = credentialSeparator) {
    const value = typeof input == 'string' && input.trim();
    return !!value ? (input.match(new RegExp(separator, 'g')) || []).length : 0;
}
function prioritizeSeparatorInputs(inputs, separator = credentialSeparator) {
    for (const input of inputs) {
        if (includesSeparator(input, separator)) {
            return input;
        }
    }
    return inputs.find((input) => typeof input == 'string' && !!input.trim());
}
function createId(input, name, organisation) {
    const value = typeof input == 'string' && input.trim();
    if (!value || (organisation && !value.includes(organisation)) || countSeparators(value) > 1) {
        if (organisation && name && organisation !== name) {
            return `${name}:${organisation}`;
        }
        else {
            return name || organisation;
        }
    }
    return value;
}
function migrationFailedLogger(subtext = 'credential') {
    logger.warn(`Unable to migrate legacy ${subtext} to latest format. Please remove from your browser.`);
}
/**
 * The default request options for the CredentialManager service.
 * @public
 */
export const defaultCredentialRequestOptions = {
    password: true,
    mediation: 'optional', // < There are a couple of options depending on the level of user input we need
};
/**
 * The default configuration for the CredentialManager service.
 * @public
 */
export const defaultCredentialManagerConfig = {
    cookie: {
        /**
         * Path was location.pathname, however it looks like axes is the only project using it, and they set it to '/'.
         * Making '/' the default. If callers want to change it they can.
         */
        path: '/',
        name: 'credentials',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        expires: 20 * 365,
        storePasswords: false,
    },
};
/**
 * The default implementation for the CredentialManager interface.
 * @public
 */
let DefaultCredentialManager = class DefaultCredentialManager {
    constructor(session, config = defaultCredentialManagerConfig) {
        this.session = session;
        this.config = config;
    }
    createBasicCredentials(inputs) {
        return {
            type: AuthType.BASIC,
            username: inputs.username,
            password: inputs.password,
        };
    }
    createRefreshCredentials(inputs) {
        var _a, _b, _c;
        return {
            type: AuthType.REFRESH,
            username: (_a = inputs === null || inputs === void 0 ? void 0 : inputs.username) !== null && _a !== void 0 ? _a : this.getUsername(),
            refreshToken: (_b = inputs === null || inputs === void 0 ? void 0 : inputs.refreshToken) !== null && _b !== void 0 ? _b : this.getRefreshToken(),
            mfaToken: (_c = inputs === null || inputs === void 0 ? void 0 : inputs.mfaToken) !== null && _c !== void 0 ? _c : this.getMFAToken(),
        };
    }
    createSSOCredentials(inputs) {
        var _a;
        return {
            type: AuthType.SSO,
            ssoToken: (_a = inputs === null || inputs === void 0 ? void 0 : inputs.ssoToken) !== null && _a !== void 0 ? _a : this.getSSOToken(),
        };
    }
    validateBasicCredentials(credentials) {
        return credentials && !!credentials.username && !!credentials.password;
    }
    validateRefreshCredentials(credentials) {
        return credentials && !!credentials.refreshToken && !!credentials.username;
    }
    validateSSOCredentials(credentials) {
        return credentials && !!credentials.ssoToken;
    }
    validateUserCredentialData(data, ensurePassword) {
        const baseResult = data &&
            !!data.id &&
            !!data.name &&
            !data.name.includes(credentialSeparator) &&
            countSeparators(data.id) <= 1;
        return ensurePassword ? baseResult && !!data.password : baseResult;
    }
    normaliseLegacyCredentialData(data, cookie) {
        const validContainerData = this.validateUserCredentialData(data, true);
        if (validContainerData) {
            return [data, true];
        }
        const validCookieData = this.validateUserCredentialData(cookie);
        if (validCookieData) {
            return [cookie, false];
        }
        /**
         * Attempt to migrate legacy PasswordCredentials
         */
        if (data) {
            try {
                const legacyData = data;
                const [name] = extractParts(legacyData.name || legacyData.id);
                const [_, organisation] = extractParts(prioritizeSeparatorInputs([legacyData.id, legacyData.name]));
                const id = createId(legacyData.id || legacyData.name, name, organisation);
                return [
                    {
                        id,
                        name,
                        password: legacyData.password || '',
                    },
                    true,
                ];
            }
            catch (err) {
                migrationFailedLogger('PasswordCredential');
            }
        }
        /**
         * Attempt to migrate legacy cookies
         */
        if (cookie) {
            try {
                const legacyCookie = cookie;
                const [name] = extractParts(legacyCookie.name || legacyCookie.username || legacyCookie.organisation);
                const [_, organisation] = extractParts(prioritizeSeparatorInputs([
                    legacyCookie.organisation || legacyCookie.username || legacyCookie.name,
                ]));
                const id = createId(legacyCookie.id || legacyCookie.username || legacyCookie.name, name, organisation);
                return [
                    {
                        id,
                        name,
                        password: legacyCookie.password || '',
                    },
                    false,
                ];
            }
            catch (err) {
                migrationFailedLogger('cookie');
            }
        }
        return [undefined, false];
    }
    storeCredentialsInContainer(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!supportsCredentialsContainer()) {
                return;
            }
            const credentials = new PasswordCredential(data);
            logger.debug('Storing credentials in CredentialsContainer');
            return navigator.credentials.store(credentials);
        });
    }
    getCredentialsFromContainer(options = defaultCredentialRequestOptions) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!supportsCredentialsContainer()) {
                return;
            }
            logger.debug('Getting credentials from CredentialsContainer');
            return (yield navigator.credentials.get(options));
        });
    }
    storeCredentialsInCookie(data) {
        const { password } = data, rest = __rest(data, ["password"]);
        const credentials = this.config.cookie.storePasswords ? data : rest;
        logger.debug('Storing credentials in cookie');
        return !!jsCookie.set(this.config.cookie.name, JSON.stringify(credentials), {
            expires: this.config.cookie.expires,
            domain: location.hostname,
            path: this.config.cookie.path,
            secure: true,
        });
    }
    getCredentialsFromCookie() {
        logger.debug('Getting credentials from cookie');
        const cookie = jsCookie.get(this.config.cookie.name);
        return cookie ? JSON.parse(cookie) : undefined;
    }
    storeCredentials(data) {
        return __awaiter(this, void 0, void 0, function* () {
            if (supportsCredentialsContainer()) {
                const credential = yield this.storeCredentialsInContainer(data);
                return Promise.resolve(!!credential);
            }
            return Promise.resolve(this.storeCredentialsInCookie(data));
        });
    }
    getCredentials(options) {
        return __awaiter(this, void 0, void 0, function* () {
            if (supportsCredentialsContainer()) {
                return this.getCredentialsFromContainer(options);
            }
            return Promise.resolve(this.getCredentialsFromCookie());
        });
    }
    getSSOToken() {
        return this.session.getSessionStorageItem('ssoToken');
    }
    getUsername() {
        return this.session.getSessionStorageItem('username');
    }
    getRefreshToken() {
        return this.session.getSessionStorageItem('refreshToken');
    }
    getMFAToken() {
        return this.session.getSessionStorageItem('mfaToken');
    }
    removeCookie() {
        jsCookie.remove(this.config.cookie.name, {
            domain: location.hostname,
            path: this.config.cookie.path,
        });
    }
    removeSSOToken() {
        this.session.removeSessionStorageItem('ssoToken');
    }
    removeUsername() {
        this.session.removeSessionStorageItem('username');
    }
    removeRefreshToken() {
        this.session.removeSessionStorageItem('refreshToken');
    }
    removeMFAToken() {
        this.session.removeSessionStorageItem('mfaToken');
    }
    removeAll() {
        this.removeCookie();
        this.removeSSOToken();
        this.removeUsername();
        this.removeRefreshToken();
        this.removeMFAToken();
    }
    preventSilentAccess() {
        return supportsCredentialsContainer() && navigator.credentials.preventSilentAccess();
    }
};
DefaultCredentialManager = __decorate([
    __param(0, Session),
    __param(1, optional(CredentialManagerConfig))
], DefaultCredentialManager);
export { DefaultCredentialManager };
/**
 * The DI token for the CredentialManager interface.
 * @internal
 */
export const CredentialManager = DI.createInterface((x) => x.singleton(DefaultCredentialManager));
